import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  errors,
  getRefreshToken,
  IAccessRefreshToken,
  SimpleResult,
} from "@ryde/shared";

import { landingRoutesApiConst } from "../constants";
import { landingCompanyIdSelector } from "../selectors";
import { useLandingApiMethods } from "./use-landing-api-methods";

export const useLandingApiAuth = () => {
  const { t } = useTranslation();
  const companyId = useRecoilValue(landingCompanyIdSelector);
  const authUrl = landingRoutesApiConst.auth(companyId as number);
  const { apiPost } = useLandingApiMethods();

  const requestOtp = useEvent(
    async ({
      phone,
      workerId,
      email,
      recaptchaToken,
    }: {
      phone: string;
      workerId: string;
      email?: string;
      recaptchaToken?: string;
    }): Promise<SimpleResult<unknown> | undefined> => {
      const response = await apiPost<SimpleResult<unknown> | undefined>(
        authUrl.requestOtpLanding,
        {
          phone,
          email,
          workerCode: workerId,
          recaptchaToken,
        },
      );

      return response?.data;
    },
  );

  const loginOtp = useEvent(
    async ({
      phone,
      email,
      verificationCode,
      recaptchaToken,
    }: {
      phone: string;
      email?: string;
      verificationCode: string;
      recaptchaToken?: string;
    }): Promise<SimpleResult<IAccessRefreshToken> | undefined> => {
      const response = await apiPost<SimpleResult<IAccessRefreshToken>>(
        authUrl.loginOtpLanding,
        { phone, email, code: verificationCode, recaptchaToken },
        undefined,
        undefined,
        false,
      );

      return response?.data;
    },
  );

  const refresh = useEvent(async (): Promise<[string, string]> => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      throw new Error(t(errors.auth.errorWhileRefreshTokens));
    }

    const response = await apiPost<IAccessRefreshToken>(
      authUrl.refreshLanding,
      undefined,
      undefined,
      {
        Authorization: `Bearer ${refreshToken}`,
      },
      false,
    );

    if (response?.data?.access_token && response?.data?.refresh_token) {
      return [response?.data?.access_token, response?.data?.refresh_token];
    }

    throw new Error(t("errors.errorWhileRefreshTokens"));
  });

  // PUBLIC
  const requestPublicOtp = useEvent(
    async ({
      phone,
      email,
      recaptchaToken,
      allowExistingPassenger = true,
    }: {
      phone?: string;
      email?: string;
      recaptchaToken?: string;
      allowExistingPassenger?: boolean;
    }): Promise<SimpleResult<unknown> | undefined> => {
      const response = await apiPost<SimpleResult<unknown> | undefined>(
        authUrl.requestPublicOtpLanding,
        {
          phone,
          email,
          recaptchaToken,
          allowExistingPassenger,
        },
      );

      return response?.data;
    },
  );

  const loginPublicOtp = useEvent(
    async ({
      phone,
      email,
      workerId,
      firstName,
      lastName,
      verificationCode,
      recaptchaToken,
      unitId,
      stationId,
    }: {
      phone?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      workerId?: string;
      verificationCode: string;
      recaptchaToken?: string;
      unitId?: number;
      stationId?: number;
    }): Promise<SimpleResult<IAccessRefreshToken> | undefined> => {
      const response = await apiPost<SimpleResult<IAccessRefreshToken>>(
        authUrl.loginPublicOtpLanding,
        {
          phone,
          email,
          workerCode: workerId,
          code: verificationCode,
          firstName,
          lastName,
          recaptchaToken,
          unitId,
          stationId,
        },
        undefined,
        undefined,
        false,
      );

      return response?.data;
    },
  );

  return { loginOtp, requestOtp, refresh, requestPublicOtp, loginPublicOtp };
};
